import { useEffect, useState, type RefObject } from 'react';

const KEYBOARD_MIN_PX = 120;
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'tel', 'url', 'number', 'password', '']);

function editableFocused(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  if (el.tagName === 'TEXTAREA' || el.isContentEditable) return true;
  return el.tagName === 'INPUT' && TEXT_INPUT_TYPES.has((el as HTMLInputElement).type);
}

/**
 * Экран как в мессенджере: fixed-оболочка ровно по видимой области. Открылась
 * клавиатура — оболочка укорачивается до её края (поле ввода над клавиатурой,
 * шапка на месте), а сдвиг, на который iOS прокрутил экран к полю, компенсируем
 * через top. Возвращает «клавиатура открыта» — нижнее меню на это время прячем.
 */
export function useViewportBox(ref: RefObject<HTMLElement | null>, enabled: boolean): boolean {
  const [keyboard, setKeyboard] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) {
      setKeyboard(false);
      return;
    }
    const vv = window.visualViewport;
    // Высота без клавиатуры: на старых Android вместе с ней сжимается и innerHeight.
    let full = vv?.height ?? window.innerHeight;

    const apply = () => {
      // Пальцами увеличили масштаб — не подстраиваемся, иначе экран «поедет».
      const tracking = !!vv && vv.scale < 1.05;
      const height = tracking ? vv!.height : window.innerHeight;
      const top = tracking ? Math.max(0, vv!.offsetTop) : 0;
      const focused = editableFocused();
      if (!focused) full = height;
      el.style.height = `${Math.round(height)}px`;
      el.style.top = `${Math.round(top)}px`;
      setKeyboard(focused && full - height > KEYBOARD_MIN_PX);
      // iOS иногда прокручивает сам документ к полю — возвращаем на место.
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };

    apply();
    vv?.addEventListener('resize', apply);
    vv?.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);
    // Фокус меняется раньше, чем клавиатура доедет: пересчитываем и после неё.
    const onFocus = () => {
      apply();
      window.setTimeout(apply, 350);
    };
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onFocus);
    return () => {
      vv?.removeEventListener('resize', apply);
      vv?.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', onFocus);
      el.style.height = '';
      el.style.top = '';
    };
  }, [ref, enabled]);

  return keyboard;
}
